import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { base44 } from '@/api/base44Client';
import { X, Loader2, CheckCircle2, Copy, Zap, Gift } from 'lucide-react';

const DISMISS_KEY = 'mr_newsletter_popup_v1';
const DISMISS_DAYS = 14;

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (ts && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
      }
    } catch (e) {}
    // Show after a short delay so it doesn't fight the hero load
    const t = setTimeout(() => setOpen(true), 2200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) {}
  };

  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await base44.functions.invoke('subscribeToNewsletter', { email, source: 'popup' });
      if (res.data?.error) {
        setErrorMsg(res.data.error);
        setStatus('error');
        return;
      }
      setStatus('success');
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText('WELCOME10');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={dismiss}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

          <motion.div
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0d0d0f] border border-white/10 overflow-hidden"
          >
            <button
              onClick={dismiss}
              className="absolute top-3 right-3 z-10 text-white/30 hover:text-white transition-colors p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Top accent strip */}
            <div className="h-1 bg-primary" />

            <div className="p-7 text-center">
              <div className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/5 text-primary text-[10px] font-bold tracking-widest px-3 py-1.5 mb-5">
                <Zap className="w-3 h-3" /> EXCLUSIVE OFFERS
              </div>

              {status === 'success' ? (
                <div className="py-4">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mx-auto mb-4 rounded-full">
                    <CheckCircle2 className="w-7 h-7 text-primary" />
                  </div>
                  <h2 className="text-white font-black text-2xl uppercase mb-2">You're In!</h2>
                  <p className="text-white/50 text-sm mb-6">
                    Check your inbox for exclusive offers. Use code <span className="text-white font-bold">WELCOME10</span> for 5% off parts &amp; 10% off experiences:
                  </p>
                  <button
                    onClick={copyCode}
                    className="w-full flex items-center justify-between border border-dashed border-primary/40 bg-primary/5 px-5 py-4 group hover:border-primary/70 transition-colors"
                  >
                    <span className="text-primary font-mono font-bold text-lg tracking-widest">WELCOME10</span>
                    {copied ? (
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    ) : (
                      <Copy className="w-4 h-4 text-primary/60 group-hover:text-primary" />
                    )}
                  </button>
                  {copied && <p className="text-primary text-xs mt-2 font-bold">Code copied!</p>}
                  <button onClick={dismiss} className="w-full mt-5 text-white/40 hover:text-white text-xs font-bold tracking-wider transition-colors">
                    CONTINUE SHOPPING
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-white font-black text-2xl md:text-3xl uppercase leading-tight mb-2">
                    Join The<br /><span className="text-primary">Inner Circle</span>
                  </h2>
                  <p className="text-white/50 text-sm mb-6 leading-relaxed">
                    Be the first to hear about new product launches, exclusive sim racing experience offers and members-only discounts.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-3 text-left">
                    <div className="flex">
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
                        placeholder="Your email address"
                        className="flex-1 min-w-0 bg-[#1a1a1f] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-primary/50"
                      />
                      <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wide px-5 transition-colors disabled:opacity-50 flex items-center justify-center"
                      >
                        {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'JOIN'}
                      </button>
                    </div>
                    {status === 'error' && (
                      <p className="text-red-400 text-xs">{errorMsg || 'Something went wrong. Please try again.'}</p>
                    )}
                  </form>

                  <div className="flex items-center justify-center gap-2 mt-4 text-white/40 text-xs">
                    <Gift className="w-3.5 h-3.5 text-primary" />
                    <span>Get 5% off parts &amp; 10% off experiences when you join</span>
                  </div>

                  <button onClick={dismiss} className="mt-5 text-white/25 hover:text-white/50 text-[11px] transition-colors">
                    No thanks, I'll pay full price
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}